import React, { Suspense, memo } from "react";
import styles from "./PostLogin.module.scss";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar/Navbar";
import Sidebar from "./components/Sidebar/Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { setSidebarOpen } from "@/store/slices/globalSlice";
import { type RootState, type AppDispatch } from "@/store/store";
import { getMyProfile } from "@/store/slices/userSlice";
import ErrorBoundary from "@/components/ErrorBoundary/ErrorBoundary";
import Loader from "@/components/Loader/Loader";
import ConfirmLogoutModal from "@/components/ConfirmLogoutModal/ConfirmLogoutModal";

const HomeLayout: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  React.useEffect(() => {
    dispatch(getMyProfile());
  }, [dispatch]);

  const sidebarOpen = useSelector((state: RootState) => state.global.sidebarOpen);

  // Align sidebar state with screen width on initial load (only once on mount)
  React.useEffect(() => {
    if (window.innerWidth <= 768) {
      dispatch(setSidebarOpen(false));
    }
  }, [dispatch]);

  // Handle transitions between mobile and desktop views during resize
  React.useEffect(() => {
    let prevIsDesktop = window.innerWidth > 768;

    const handleResize = () => {
      const isDesktop = window.innerWidth > 768;
      if (isDesktop !== prevIsDesktop) {
        dispatch(setSidebarOpen(isDesktop));
        prevIsDesktop = isDesktop;
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch]);

  const toggleSidebarOpen = (flag: boolean) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (sidebarOpen !== flag) {
      dispatch(setSidebarOpen(flag));
    }
  };

  return (
    <>
      <ConfirmLogoutModal />
      <div className={styles.layout}>
        {sidebarOpen && (
          <div
            className={styles.sidebarBackdrop}
            onClick={toggleSidebarOpen(false)}
          />
        )}
        <Sidebar toggleSidebarOpen={toggleSidebarOpen} />
        <div className={styles.rightSideContainer}>
          {/* Single unified Navbar for ALL pages — matches HTML prototype */}
          <Navbar />
          <div className={styles.outletDiv}>
            <Suspense fallback={<Loader />}>
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </Suspense>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(HomeLayout);
